import { useCallback, useEffect, useMemo, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { GestaoDistribuicaoEpi } from "../../domain/models/gestao_distribuicao_epi";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { GestaoDistribuicaoEpiForm } from "../schemas/DistribuicaoEpi_lista/gestao_distribuicao_epi";
import {
  initialDistribuicaoEpiListaState,
  useDistribuicaoEpiListaFilters,
} from "../hooks/DistribuicaoEpi_lista/filters";
import { useDistribuicaoEpiListaPagination } from "../hooks/DistribuicaoEpi_lista/pagination";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { GestaoRecebimentoEpiDropdown } from "../../domain/models/gestao_recebimento_epi_dropdown";
import { CargoDropDown } from "../../../../common/domain/models/cargo_dropdown";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { n } from "react-router/dist/development/index-react-server-client-BKpa2trA";
import { GryfoAudit } from "../../domain/models/gryfo_audit";
import { GridRowId } from "@mui/x-data-grid";
import { DistribuicaoEpiItem } from "../../domain/models/gestao_distribuicao_item";

type LoadGestaoDistribuicaoEpiListaFuncState = {
  idsGestaoEpi?: number[] | null;
  idsFuncionarios?: number[] | null;
  ca?: string | null;
  dataEntrega?: (Date | null)[] | null;
  page?: number;
  rowsPerPage?: number;
};

export const useDistribuicaoEpiListaViewModel = () => {
  const {
    commons: {
      getCargosDropDownUseCase,
      getFuncionariosDropDownUseCase,
      getCentroCustoDropDownUseCase,
    },
    gestaoEpi: {
      getGestaoRecebimentoEpiDropDownUseCase,
      getAllGestaoDistribuicaoEpiUseCase,
      createGestaoDistribuicaoEpiUseCase,
      updateGestaoDistribuicaoEpiUseCase,
      deleteGestaoDistribuicaoEpiUseCase,
      validacaoDistribuicaoEpiUseCase,
      getBiometriaUseCase,
      getBiometriaAuditoriaUseCase,
    },
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification, pushWarnNotification } =
    usePageLoader();

  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [
    gestaoDistribuicaoEpiCadastroModal,
    setGestaoDistribuicaoEpiCadastroModal,
  ] = useState(false);
  const [
    gestaoDistribuicaoEpiDeleteModal,
    setGestaoDistribuicaoEpiDeleteModal,
  ] = useState(false);
  const [validacaoFuncionarioModal, setValidacaoFuncionarioModal] =
    useState(false);

  const [gestaoRecebimentoEpiDropdown, setGestaoRecebimentoEpiDropdown] =
    useState<GestaoRecebimentoEpiDropdown[]>();
  const [funcionariosDropdown, setFuncionariosDropdown] =
    useState<FuncionarioDropDown[]>();

  const [gestaoDistribuicaoEpiLista, setGestaoDistribuicaoEpiLista] =
    useState<GestaoDistribuicaoEpi[]>();
  const [selectedDistribuicaoEpi, setSelectedGestaoDistribuicaoEpi] =
    useState<GestaoDistribuicaoEpi>();
  const [cargosDropDown, setCargosDropDown] = useState<CargoDropDown[]>();
  const [centroCustoDropdown, setCentroCustoDropdown] =
    useState<CentroCustoDropDown[]>();
  const [centroCustoSaidaDropdown, setCentroCustoSaidaDropdown] =
    useState<CentroCustoDropDown[]>();
  const [distribuicaoEpiItemLista, setDistribuicaoEpiItemLista] = useState<
    DistribuicaoEpiItem[]
  >([]);
  const [imagemUrl, setImagemUrl] = useState<any>(null);
  const [modalImagemOpen, setModalImagemOpen] = useState<boolean>(false);
  const [gryfoAuditOpen, setModalGryfoAudit] = useState<boolean>(false);
  const [selectedGryfoAudit, setSelectedGryfoAudit] = useState<GryfoAudit>();
  const [idAguardandoValidacao, setIdAguardandoValidacao] = useState<
    number | null
  >(null);
  const [modalVisualizarAberto, setModalVisualizarAberto] =
    useState<boolean>(false);
  const [distribuicaoParaVisualizar, setDistribuicaoParaVisualizar] =
    useState<any>(null);

  const filters = useDistribuicaoEpiListaFilters();
  const pagination = useDistribuicaoEpiListaPagination();

  const filteredFuncionariosDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter((x) => {
      if (selectedDistribuicaoEpi) {
        if (x.idFuncionario === selectedDistribuicaoEpi.idFuncionario) {
          return true;
        }
      }
      return x.canBeSelected === true;
    });
  }, [selectedDistribuicaoEpi, funcionariosDropdown]);

  const loadCargosDropDown = useCallback(async () => {
    try {
      const cargos = await getCargosDropDownUseCase.getCargosDropDown();
      setCargosDropDown(cargos);
    } catch (e: unknown) {
      throw e;
    }
  }, [getCargosDropDownUseCase]);

  const loadCentroCustoDropdown = useCallback(async () => {
    try {
      const centroCusto =
        await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setCentroCustoDropdown(centroCusto);

      setCentroCustoSaidaDropdown(centroCusto);
    } catch (e: unknown) {
      throw e;
    }
  }, [getCentroCustoDropDownUseCase]);

  /**
   * Funcoes de carregamento
   */

  const loadGestaoEpiDropdown = useCallback(
    async (idCentroCusto: number) => {
      try {
        const results =
          await getGestaoRecebimentoEpiDropDownUseCase.getGestaoRecebimentoEpiDropDown(
            idCentroCusto,
          );

        setGestaoRecebimentoEpiDropdown(results); // Atualiza o state

        // 👇 O SEGREDO AQUI: Devolva os resultados para quem chamou!
        return results;
      } catch (e: unknown) {
        setGestaoRecebimentoEpiDropdown([]);
        // 👇 Retorna vazio para não quebrar a abertura do modal em caso de erro
        return [];
      }
    },
    [getGestaoRecebimentoEpiDropDownUseCase],
  );

  const loadFuncionariosDropDown = useCallback(async () => {
    try {
      const results =
        await getFuncionariosDropDownUseCase.getFuncionariosDropDown(
          true,
          false,
          true,
        );
      setFuncionariosDropdown(results);
    } catch (e: unknown) {
      setFuncionariosDropdown(undefined);
      throw new Error("Falha ao buscar lista de epis!");
    }
  }, [getFuncionariosDropDownUseCase]);

  const loadGestaoDistribuicaoEpiLista = useCallback(
    async (fs?: LoadGestaoDistribuicaoEpiListaFuncState) => {
      try {
        const filteredIdsGestaoEpi = evaluateParamValue(
          fs?.idsGestaoEpi,
          filters.idsGestaoEpi,
        );
        const filteredIdsFuncionarios = evaluateParamValue(
          fs?.idsFuncionarios,
          filters.idsFuncionarios,
        );
        const filteredCa = evaluateParamValue(fs?.ca, filters.ca);
        const filteredDataEntrega = evaluateParamValue(
          fs?.dataEntrega,
          filters.dataEntrega,
        );

        const page = fs?.page ?? pagination.page;
        const rowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllGestaoDistribuicaoEpiUseCase.getAllGestaoDistribuicaoEpi(
          filteredIdsGestaoEpi,
          filteredIdsFuncionarios,
          filteredCa,
          filteredDataEntrega,
          page,
          rowsPerPage,
        );
        setGestaoDistribuicaoEpiLista(getAllGestaoDistribuicaoEpiUseCase.items);
        pagination.setTotalItems(getAllGestaoDistribuicaoEpiUseCase.totalItems);
      } catch (e: unknown) {
        setGestaoDistribuicaoEpiLista(undefined);
        throw new Error("Falha ao buscar lista de epis!");
      }
    },
    [filters, pagination, getAllGestaoDistribuicaoEpiUseCase],
  );

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadFuncionariosDropDown();
      await loadCargosDropDown();
      await loadCentroCustoDropdown();
      await loadGestaoDistribuicaoEpiLista();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadFuncionariosDropDown();
      await loadCargosDropDown();
      await loadCentroCustoDropdown();
      await loadGestaoDistribuicaoEpiLista();
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao recarregar pagina!"),
        { isAutoFinish: true },
      );
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionariosDropDown,
    loadCargosDropDown,
    loadCentroCustoDropdown,
    loadGestaoDistribuicaoEpiLista,
    pushErrorNotification,
  ]);

  const handleCadastroGestaoDistribuicaoEpiModalOpenState = useCallback(
    async (idGestaoDistribuicaoEpi: number | null, openState: boolean) => {
      if (openState) {
        if (idGestaoDistribuicaoEpi && gestaoDistribuicaoEpiLista) {
          const distribuicaoSelecionada = gestaoDistribuicaoEpiLista.find(
            (x) => x.idGestaoDistribuicaoEpi === idGestaoDistribuicaoEpi,
          );

          setSelectedGestaoDistribuicaoEpi(distribuicaoSelecionada);

          if (distribuicaoSelecionada && distribuicaoSelecionada.itens) {
            let episParaFiltrar = gestaoRecebimentoEpiDropdown;
            if (distribuicaoSelecionada.idCentroCustoSaida) {
              const dadosRecentes = await loadGestaoEpiDropdown(
                distribuicaoSelecionada.idCentroCustoSaida,
              );

              if (dadosRecentes && dadosRecentes.length > 0) {
                episParaFiltrar = dadosRecentes; 
              }
            }

            const itensParaGrid = distribuicaoSelecionada.itens.map(
              (item, index) => {
                const epiData = episParaFiltrar?.find(
                  (x: any) =>
                    x.idGestaoRecebimentoEpi === item.idGestaoRecebimentoEpi,
                );

                const nomeCompleto = epiData?.nomeEpi || "EPI não encontrado";
                const nomeLimpo = nomeCompleto.includes("-")
                  ? nomeCompleto.split("-")[0].trim()
                  : nomeCompleto;

                return {
                  idGestaoDistribuicaoEpiItem: -(index + 1),
                  idGestaoRecebimentoEpi: item.idGestaoRecebimentoEpi,
                  quantidade: item.quantidade,

                  nomeEpi: nomeLimpo,
                  ca: epiData?.ca || "",

                  idGestaoDistribuicaoEpi:
                    distribuicaoSelecionada.idGestaoDistribuicaoEpi,
                };
              },
            );

            setDistribuicaoEpiItemLista(itensParaGrid);
          } else {
            setDistribuicaoEpiItemLista([]);
          }
        } else {
          setSelectedGestaoDistribuicaoEpi(undefined);
          setDistribuicaoEpiItemLista([]);
        }
      } else {
        setDistribuicaoEpiItemLista([]);
      }

      setGestaoDistribuicaoEpiCadastroModal(openState);
    },
    [
      gestaoDistribuicaoEpiLista,
      gestaoRecebimentoEpiDropdown,
      loadGestaoEpiDropdown,
    ],
  );
  const handleValidacaoFuncionarioModalOpenState = useCallback(
    (idGestaoDistribuicaoEpi: number | null, openState: boolean) => {
      if (openState) {
        if (idGestaoDistribuicaoEpi && gestaoDistribuicaoEpiLista) {
          setSelectedGestaoDistribuicaoEpi(
            gestaoDistribuicaoEpiLista.find(
              (x) => x.idGestaoDistribuicaoEpi === idGestaoDistribuicaoEpi,
            ),
          );
        } else {
          setSelectedGestaoDistribuicaoEpi(undefined);
        }
      }
      setValidacaoFuncionarioModal(openState);
    },
    [gestaoDistribuicaoEpiLista],
  );

  useEffect(() => {
    if (idAguardandoValidacao && gestaoDistribuicaoEpiLista) {
      const itemEncontrado = gestaoDistribuicaoEpiLista.find(
        (x) => x.idGestaoDistribuicaoEpi === idAguardandoValidacao,
      );

      if (itemEncontrado) {
        setSelectedGestaoDistribuicaoEpi(itemEncontrado);
        setValidacaoFuncionarioModal(true);
        setIdAguardandoValidacao(null); // Limpa a variável para não ficar abrindo sozinho depois
      }
    }
  }, [gestaoDistribuicaoEpiLista, idAguardandoValidacao]);

  const handleGestaoDistribuicaoEpiSubmit = useCallback(
    async (form: GestaoDistribuicaoEpiForm) => {
      try {
        setSaving(true);

        if (
          !distribuicaoEpiItemLista ||
          distribuicaoEpiItemLista.length === 0
        ) {
          pushErrorNotification(
            "Atenção",
            "Você precisa incluir pelo menos um EPI na tabela antes de salvar!",
          );
          return;
        }

        const payloadItens = distribuicaoEpiItemLista.map((item) => ({
          idGestaoRecebimentoEpi: item.idGestaoRecebimentoEpi ?? 0,
          quantidade: item.quantidade ?? 0,
        }));

        if (selectedDistribuicaoEpi) {
          await updateGestaoDistribuicaoEpiUseCase.updateGestaoDistribuicaoEpi(
            selectedDistribuicaoEpi.idGestaoDistribuicaoEpi,
            payloadItens,
            form.idFuncionario,
            form.dataEntrega,
            form.idCentroCustoSaida,
            form.idCentroCusto,
          );
          setGestaoDistribuicaoEpiCadastroModal(false);
          setSelectedGestaoDistribuicaoEpi(undefined);
        } else {
          const idGestaoDistribuicaoEpi =
            await createGestaoDistribuicaoEpiUseCase.createGestaoDistribuicaoEpi(
              payloadItens,
              form.idFuncionario,
              form.dataEntrega,
              form.idCentroCustoSaida,
              form.idCentroCusto,
            );

          if (idGestaoDistribuicaoEpi) {
            setIdAguardandoValidacao(idGestaoDistribuicaoEpi);
            setGestaoDistribuicaoEpiCadastroModal(false);
          }
        }
        await loadGestaoDistribuicaoEpiLista();
        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar distribuição de epi!"),
          { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
      }
    },
    [
      selectedDistribuicaoEpi,
      distribuicaoEpiItemLista,
      loadGestaoDistribuicaoEpiLista,
      pushInfoNotification,
      updateGestaoDistribuicaoEpiUseCase,
      createGestaoDistribuicaoEpiUseCase,
      pushErrorNotification,
    ],
  );

  const handleDeleteGestaoDistribuicaoEpiModalOpenState = useCallback(
    (idGestaoDistribuicaoEpi: number | null, openState: boolean) => {
      if (openState) {
        if (idGestaoDistribuicaoEpi && gestaoDistribuicaoEpiLista) {
          setSelectedGestaoDistribuicaoEpi(
            gestaoDistribuicaoEpiLista.find(
              (x) => x.idGestaoDistribuicaoEpi === idGestaoDistribuicaoEpi,
            ),
          );
        } else {
          setSelectedGestaoDistribuicaoEpi(undefined);
        }
      }
      setGestaoDistribuicaoEpiDeleteModal(openState);
    },
    [gestaoDistribuicaoEpiLista],
  );

  const handleGestaoDistribuicaoEpiDeleteSubmit = useCallback(async () => {
    try {
      setSaving(true);
      if (selectedDistribuicaoEpi) {
        await deleteGestaoDistribuicaoEpiUseCase.deleteGestaoDistribuicaoEpi(
          selectedDistribuicaoEpi.idGestaoDistribuicaoEpi,
        );
      }
      setGestaoDistribuicaoEpiDeleteModal(false);
      setSelectedGestaoDistribuicaoEpi(undefined);
      await loadGestaoDistribuicaoEpiLista();
      pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao excluir distribuição de epi!"),
        { isAutoFinish: true },
      );
    } finally {
      setSaving(false);
    }
  }, [
    selectedDistribuicaoEpi,
    deleteGestaoDistribuicaoEpiUseCase,
    loadGestaoDistribuicaoEpiLista,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useDistribuicaoEpiListaPagination.setState({ page: page + 1 });
        await loadGestaoDistribuicaoEpiLista({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao recarregar pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [loadGestaoDistribuicaoEpiLista, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useDistribuicaoEpiListaPagination.setState({ page: 1, rowsPerPage });
        await loadGestaoDistribuicaoEpiLista({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao recarregar pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [loadGestaoDistribuicaoEpiLista, pushErrorNotification],
  );

  const handleGestaoEpiChange = useDebouncedCallback(
    debouncedHandler,
    async (ids: number[] | null) => {
      useDistribuicaoEpiListaFilters.setState({ idsGestaoEpi: ids });
      return async () => {
        try {
          setLoading(true);
          useDistribuicaoEpiListaPagination.setState({ page: 1 });
          await loadGestaoDistribuicaoEpiLista({ idsGestaoEpi: ids, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados da pagina!"),
            { isAutoFinish: true },
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadGestaoDistribuicaoEpiLista, pushErrorNotification],
  );

  const handleFuncionariosChange = useDebouncedCallback(
    debouncedHandler,
    async (ids: number[] | null) => {
      useDistribuicaoEpiListaFilters.setState({ idsFuncionarios: ids });
      return async () => {
        try {
          setLoading(true);
          useDistribuicaoEpiListaPagination.setState({ page: 1 });
          await loadGestaoDistribuicaoEpiLista({
            idsFuncionarios: ids,
            page: 1,
          });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados da pagina!"),
            { isAutoFinish: true },
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadGestaoDistribuicaoEpiLista, pushErrorNotification],
  );

  const handleCaChange = useDebouncedCallback(
    debouncedHandler,
    async (ca: string | null) => {
      useDistribuicaoEpiListaFilters.setState({ ca });
      return async () => {
        try {
          setLoading(true);
          useDistribuicaoEpiListaPagination.setState({ page: 1 });
          await loadGestaoDistribuicaoEpiLista({ ca, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados da pagina!"),
            { isAutoFinish: true },
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadGestaoDistribuicaoEpiLista, pushErrorNotification],
  );

  const handleDataEntregaChange = useDebouncedCallback(
    debouncedHandler,
    async (dataEntrega: (Date | null)[] | null) => {
      useDistribuicaoEpiListaFilters.setState({ dataEntrega });
      return async () => {
        try {
          setLoading(true);
          useDistribuicaoEpiListaPagination.setState({ page: 1 });
          await loadGestaoDistribuicaoEpiLista({ dataEntrega, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados da pagina!"),
            { isAutoFinish: true },
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadGestaoDistribuicaoEpiLista, pushErrorNotification],
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useDistribuicaoEpiListaFilters.setState({
        ...initialDistribuicaoEpiListaState,
      });
      useDistribuicaoEpiListaPagination.setState({ page: 1 });
      await loadGestaoDistribuicaoEpiLista({
        ...initialDistribuicaoEpiListaState,
        page: 1,
      });
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
        { isAutoFinish: true },
      );
    } finally {
      setLoading(false);
    }
  }, [loadGestaoDistribuicaoEpiLista, pushErrorNotification]);

  const handleValidacaoFuncionarioError = useCallback(
    (msg: string) => {
      pushErrorNotification("Erro", [msg], { isAutoFinish: true });
    },
    [pushErrorNotification],
  );

  const handleValidarFuncionarioSubmit = useCallback(
    async (foto: string) => {
      if (selectedDistribuicaoEpi) {
        try {
          setSaving(true);

          await validacaoDistribuicaoEpiUseCase.validacaoDistribuicaoEpi(
            selectedDistribuicaoEpi.idGestaoDistribuicaoEpi,
            foto,
          );

          setSelectedGestaoDistribuicaoEpi(undefined);
          setGestaoDistribuicaoEpiCadastroModal(false);
          setValidacaoFuncionarioModal(false);
          await loadGestaoDistribuicaoEpiLista();
          pushInfoNotification("Sucesso", "Validação realizada com sucesso!");
        } catch (e: unknown) {
          pushWarnNotification(
            "Alerta",
            processError(e, "Falha ao validar!")[0],
          );
        } finally {
          setSaving(false);
        }
      }
    },
    [
      selectedDistribuicaoEpi,
      validacaoDistribuicaoEpiUseCase,
      loadGestaoDistribuicaoEpiLista,
      pushInfoNotification,
      pushWarnNotification,
    ],
  );
  const handleActionVisualize = useCallback(
    async (idGestaoDistribuicaoEpi: number) => {
      if (gestaoDistribuicaoEpiLista && gestaoDistribuicaoEpiLista.length > 0) {
        const gestaoDistribuicaoEpi = gestaoDistribuicaoEpiLista.find(
          (x) => x.idGestaoDistribuicaoEpi === idGestaoDistribuicaoEpi,
        );
        if (gestaoDistribuicaoEpi) {
          try {
            setLoading(true);
            setSelectedGestaoDistribuicaoEpi(
              gestaoDistribuicaoEpiLista.find(
                (x) => x.idGestaoDistribuicaoEpi === idGestaoDistribuicaoEpi,
              ),
            );
            const url = await getBiometriaUseCase.getBiometria(
              gestaoDistribuicaoEpi.idGestaoDistribuicaoEpi,
              "",
            );

            setImagemUrl(url);
            setModalImagemOpen(true);
          } catch (e: unknown) {
            pushErrorNotification(
              "Erro",
              processError(e, "Falha ao buscar imagens!"),
              { isAutoFinish: true },
            );
          } finally {
            setLoading(false);
          }
        }
      }
    },
    [gestaoDistribuicaoEpiLista, getBiometriaUseCase, pushErrorNotification],
  );
  const handleActionAuditoria = useCallback(
    async (idGestaoDistribuicaoEpi: number) => {
      if (gestaoDistribuicaoEpiLista && gestaoDistribuicaoEpiLista.length > 0) {
        const gestaoDistribuicaoEpi = gestaoDistribuicaoEpiLista.find(
          (x) => x.idGestaoDistribuicaoEpi === idGestaoDistribuicaoEpi,
        );
        if (gestaoDistribuicaoEpi) {
          try {
            setLoading(true);
            const url =
              await getBiometriaAuditoriaUseCase.getBiometriaAuditoria(
                gestaoDistribuicaoEpi.idGestaoDistribuicaoEpi ?? "",
              );

            setImagemUrl(url);
            setSelectedGryfoAudit(url);
            setModalGryfoAudit(true);
          } catch (e: unknown) {
            pushWarnNotification(
              "Alerta",
              processError(e, "Falha ao validar!")[0],
            );
          } finally {
            setLoading(false);
          }
        }
      }
    },
    [
      gestaoDistribuicaoEpiLista,
      getBiometriaAuditoriaUseCase,
      pushWarnNotification,
    ],
  );

  const handleCloseModalImagem = useCallback(() => {
    setModalImagemOpen(false);
    if (imagemUrl) {
      window.URL.revokeObjectURL(imagemUrl);
      setImagemUrl(null);
    }
  }, [imagemUrl]);

  const handleCloseModalGryfoAudit = useCallback(() => {
    setModalGryfoAudit(false);
    if (imagemUrl) {
      window.URL.revokeObjectURL(imagemUrl);
      setImagemUrl(null);
    }
  }, [imagemUrl]);

  const handleQuantidadeChange = useCallback(
    (novaQtd: number | null, idLinhaGrid: GridRowId) => {
      setDistribuicaoEpiItemLista((listaAtual) =>
        listaAtual.map((item) =>
          item.idGestaoDistribuicaoEpiItem === Number(idLinhaGrid)
            ? { ...item, quantidade: novaQtd }
            : item,
        ),
      );
    },
    [],
  );

  const handleIncludeGrid = useCallback(
    (idsEpis: number[], quantidade: number) => {
      const novosItens = [...distribuicaoEpiItemLista];
      let contadorFake = -1;
      
      const idsTemporarios = novosItens
        .map((x) => x.idGestaoDistribuicaoEpiItem ?? 0)
        .filter((id) => id < 0);

      if (idsTemporarios.length > 0) {
        contadorFake = Math.min(...idsTemporarios) - 1;
      }

      idsEpis.forEach((idEpi) => {
        const epiData = gestaoRecebimentoEpiDropdown?.find(
          (x) => x.idGestaoRecebimentoEpi === idEpi,
        );
        
        const nomeCompleto = epiData?.nomeEpi || "EPI não encontrado";
        const nomeLimpo = nomeCompleto.includes("-")
          ? nomeCompleto.split("-")[0].trim()
          : nomeCompleto;

        novosItens.push({
          idGestaoDistribuicaoEpiItem: contadorFake,
          idGestaoRecebimentoEpi: idEpi,
          quantidade: quantidade,
          nomeEpi: nomeLimpo,
          idGestaoDistribuicaoEpi: 0,
          ca: epiData?.ca || "",
        });
        
        contadorFake--;
      });

      setDistribuicaoEpiItemLista(novosItens);
    },
    [distribuicaoEpiItemLista, gestaoRecebimentoEpiDropdown],
  );

  const handleRemoveGridItem = useCallback((idLinhaGrid: GridRowId) => {
    setDistribuicaoEpiItemLista((listaAtual) =>
      listaAtual.filter(
        (item) => item.idGestaoDistribuicaoEpiItem !== Number(idLinhaGrid),
      ),
    );
  }, []);

  const handleAbrirVisualizacao = async (row: any) => {
    setDistribuicaoParaVisualizar(row);
    setModalVisualizarAberto(true);
    if (row.idCentroCustoSaida) {
      await loadGestaoEpiDropdown(row.idCentroCustoSaida);
    }
  };

  return {
    loading,
    saving,
    errorMessage,
    gestaoDistribuicaoEpiCadastroModal,
    gestaoDistribuicaoEpiDeleteModal,
    validacaoFuncionarioModal,
    gestaoRecebimentoEpiDropdown: gestaoRecebimentoEpiDropdown ?? [],
    funcionariosDropdown: filteredFuncionariosDropdown ?? [],
    gestaoDistribuicaoEpiLista: gestaoDistribuicaoEpiLista ?? [],
    selectedDistribuicaoEpi: selectedDistribuicaoEpi ?? null,
    cargosDropDown: cargosDropDown ?? [],
    centroCustoDropdown: centroCustoDropdown ?? [],
    centroCustoSaidaDropdown: centroCustoSaidaDropdown ?? [],
    filters,
    pagination,
    modalImagemOpen,
    gryfoAuditOpen,
    imagemUrl,
    selectedGryfoAudit,
    distribuicaoEpiItemLista,
    modalVisualizarAberto,
    distribuicaoParaVisualizar,
    setDistribuicaoParaVisualizar,
    setModalVisualizarAberto,
    handleReload,
    handleCadastroGestaoDistribuicaoEpiModalOpenState,
    handleGestaoDistribuicaoEpiSubmit,
    handleDeleteGestaoDistribuicaoEpiModalOpenState,
    handleGestaoDistribuicaoEpiDeleteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleGestaoEpiChange,
    handleFuncionariosChange,
    handleCaChange,
    handleDataEntregaChange,
    handleClearFilters,
    handleValidacaoFuncionarioModalOpenState,
    handleValidacaoFuncionarioError,
    handleValidarFuncionarioSubmit,
    handleActionVisualize,
    handleCloseModalImagem,
    handleActionAuditoria,
    handleCloseModalGryfoAudit,
    loadGestaoEpiDropdown,
    handleQuantidadeChange,
    handleIncludeGrid,
    handleRemoveGridItem,
    handleAbrirVisualizacao,
  };
};
